import { Alert, Button, Card, Col, Input, App, Row, Tag, Form, Typography, theme } from 'antd';
import React, { FC, useEffect, useState, useMemo, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { EditUserItem, UserItem, UserItemStore, UserProfileItemStore, UserSummaryStore } from 'src/stores/users-store';
import { AppConfig, container } from 'src/inversify.config';
import { nameof } from 'src/core/utils/object';
import { formatMessage } from 'src/core/services/http.service';
import LanguageSelect from 'src/common/components/business/language-select';
import { useIdentity } from '../../../core/services/authentication';
import i18next from 'i18next';
import { ENV } from 'src/';
import { useNavigate } from 'react-router-dom';
import SelectCountry from 'src/common/components/business/country-select';
import { AppConfiguration } from 'src/core/services/configuration.service';
import { RuleObject } from 'antd/es/form';
import { curateString } from 'src/core/utils/object';
import { useCurrentUserTenantPermissionsFilter } from 'src/common/permission-hooks';
import { PermissionsConstants } from 'src/common/types';
import { CheckOutlined, CloseOutlined, LoginOutlined } from '@ant-design/icons';
const { Text } = Typography;
const { useToken } = theme;

const permissions = [PermissionsConstants.ManageAdminOrganization];

const UserGeneralPropertyEditCard: FC<{ userId: string }> = ({ userId }) => {
  const identity = useIdentity();
  const [userPermissionsSet, checkingPermissions] = useCurrentUserTenantPermissionsFilter(identity, permissions);
  const hasManageAdminOrganitionsPermission = !checkingPermissions && userPermissionsSet.has(PermissionsConstants.ManageAdminOrganization);

  const { t } = useTranslation();
  const config: AppConfiguration = useContext(AppConfig);
  const [form] = Form.useForm();
  const userStore = container.get(UserItemStore);
  const userProfileStore = container.get(UserProfileItemStore);
  const userProfileState = userProfileStore.state;

  const userState = userStore.state;
  const usersStore = useMemo(() => container.get(UserSummaryStore), []);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { modal } = App.useApp();
  const { token } = useToken();

  useEffect(() => {
    form.resetFields();
  }, [userState.item.value]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (userProfileState?.item?.value && userState?.item?.value && userProfileState?.item?.email?.value === userState?.item?.email?.value)
      userState.set((s) => {
        s = {
          ...s,
          item: {
            ...s?.item,
            defaultLanguage: userProfileState.value.item.defaultLanguage,
            displayName: userProfileState.value.item.displayName,
            email: userProfileState.value.item.email,
          },
        };
        return s;
      });
  }, [userProfileState]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSave = async () => {
    let item = await form.validateFields();
    item.displayName = curateString(item.displayName);
    const response = await userStore.save(userId, item as UserItem);
    if (response?.isSuccess) {
      if (identity.email === userState.item.email.value) {
        i18next.changeLanguage(item.defaultLanguage);
        document.cookie = `language=${item.defaultLanguage}`;
        userProfileState.set((s) => {
          s = { ...s, item: { ...s?.item, defaultLanguage: item.defaultLanguage, displayName: item.displayName, email: item.email } };
          return s;
        });
      }
      userState.set((s) => {
        s.item.displayName = item.displayName;
        s.item.defaultLanguage = item.defaultLanguage;
        s.item.country = item.country;
        return s;
      });
      form.resetFields();
    } else setError(formatMessage(response));
  };

  const onReset = () => {
    form.resetFields();
  };

  const deleteUser = async () => {
    const response = await usersStore.delete(userId);
    if (!response.isSuccess && response.error) {
      modal.error({
        title: t('SomethingWentWrong'),
        content: config.environment === ENV.DEV ? response.error : t('ErrorDeletingUser'),
        okText: t('OK'),
        cancelText: t('Cancel'),
      });
    } else {
      navigate(`/users`);
      modal.info({
        title: t('UserDeleted').replace('{0}', `${userState.item.value?.displayName}`).replace('{1}', `${userState.item.value?.email}`),
        okText: t('OK'),
      });
    }
  };

  const onDelete = async () => {
    if (userId === identity.profile['sub']) {
      modal.error({
        title: t('CanNotDeleteOwnUser'),
        okText: t('OK'),
        cancelText: t('Cancel'),
      });
    } else {
      modal.confirm({
        title: t('ConfirmDeleteUser').replace('{0}', `${userState.item.value?.displayName}`).replace('{1}', `${userState.item.value?.email}`),
        icon: null,
        onOk: deleteUser,
        okText: t('OK'),
        cancelText: t('Cancel'),
        cancelButtonProps: { type: 'default' },
      });
    }
  };

  return (
    <Card title={<h3 style={{ textAlign: 'left', marginLeft: 16, textWrap: 'wrap' }}>{t('GeneralProperties')}</h3>}>
      {error.length !== 0 && <Alert type="error" message={t(error || '')} />}
      <Form initialValues={userState.item.value} form={form} layout="vertical" autoComplete="off" style={{ margin: '16px 40px 40px 40px' }}>
        <Form.Item
          name={nameof<UserItem>('email')}
          label={t('Email')}
          rules={[
            () => ({
              validator: (ruleObj: RuleObject, value: string) => {
                const maxLenght = 256;
                if (value === undefined) return Promise.resolve();
                if (value.length > maxLenght) {
                  return Promise.reject(t('TextFieldLimitError').replace('{0}', t('Email')).replace('{1}', `${maxLenght}`));
                } else return Promise.resolve();
              },
            }),
          ]}
        >
          <Input value={userState.item.value?.email} readOnly autoComplete="off" />
        </Form.Item>
        <Form.Item
          name={nameof<EditUserItem>('displayName')}
          label={t('DisplayName')}
          rules={[
            { required: true, message: t('PleaseEnterName') },
            () => ({
              validator: (ruleObj: RuleObject, value: string) => {
                const maxLenght = 128;
                if (value === undefined) return Promise.resolve();
                if (value.length > maxLenght) {
                  return Promise.reject(t('TextFieldLimitError').replace('{0}', t('DisplayName')).replace('{1}', `${maxLenght}`));
                } else return Promise.resolve();
              },
            }),
          ]}
        >
          <Input autoComplete="off" allowClear placeholder={t('DisplayName')} />
        </Form.Item>
        <Form.Item label={t('Organizations')}>
          <span>
            {(userState.item.value?.organizations ?? []).map((org) => (
              <Tag style={{ fontSize: 16, padding: '6px' }} key={org.organizationDisplayName}>
                {org.organizationDisplayName}
              </Tag>
            ))}
          </span>
        </Form.Item>
        <Form.Item name="defaultLanguage" label={t('DefaultLanguage')} rules={[{ required: false }]}>
          <LanguageSelect />
        </Form.Item>
        {hasManageAdminOrganitionsPermission ? (
          <Form.Item name={nameof<UserItem>('country')} label={t('Country')}>
            <SelectCountry />
          </Form.Item>
        ) : (
          <Form.Item>
            <span>
              {t('Country')}: {userState.item.value?.country}
            </span>
          </Form.Item>
        )}

        <Form.Item>
          <Row gutter={[16, 8]} style={{ marginBottom: 8 }}>
            <Col span={24}>
              <div style={{ marginBottom: 4 }}>
                <Text strong>{t('MFAStatus')}: </Text>
                {userState?.item?.twoFactorEnabled?.value && (
                  <>
                    <CheckOutlined style={{ color: token.colorSuccess, marginLeft: 4, marginRight: 4 }} />
                    <Text style={{ color: token.colorSuccess }}>{t('Enabled')}</Text>
                  </>
                )}
                {!userState?.item?.twoFactorEnabled?.value && (
                  <>
                    <CloseOutlined style={{ color: token.colorError, marginLeft: 4, marginRight: 4 }} />
                    <Text style={{ color: token.colorError }}>{t('Disabled')}</Text>
                  </>
                )}
              </div>
              <Button
                type="default"
                disabled={userState.isBusy.value}
                onClick={async () => {
                  const result = await userStore.switchMFA(userId, !(userState?.item?.twoFactorEnabled?.value ?? false));
                  if (result?.isSuccess) {
                    userState.set((s) => {
                      s = {
                        ...s,
                        item: {
                          ...s?.item,
                          twoFactorEnabled: !s?.item.twoFactorEnabled,
                        },
                      };
                      return s;
                    });
                  }
                }}
                icon={<LoginOutlined />}
                style={{ width: '100%', height: 'auto', minHeight: '60px', textWrap: 'wrap' }}
              >
                {userState?.item?.twoFactorEnabled?.value ? t('DisableMFA') : t('EnableMFA')}
              </Button>
            </Col>
            <Col lg={12} xs={24} style={{ minWidth: 80 }}>
              <Button htmlType="button" type="default" disabled={userState.isBusy.value} onClick={onReset} style={{ width: '100%' }}>
                {t('Reset')}
              </Button>
            </Col>
            <Col lg={12} xs={24} style={{ minWidth: 130 }}>
              <Button
                type="primary"
                disabled={userState.isBusy.value}
                onClick={onSave}
                style={{ width: '100%', height: 'auto', minHeight: '60px', textWrap: 'wrap' }}
              >
                {t('SaveChanges')}
              </Button>
            </Col>
          </Row>
          <Button type="default" disabled={userState.isBusy.value} onClick={onDelete} style={{ width: '100%' }}>
            {t('DeleteUser')}
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default UserGeneralPropertyEditCard;
